/** A short-lived, single-owner read cache. Aborting one view never cancels another view's read. */
export function createScopedReadCache<T>(ttlMs: number) {
  let owner: unknown;
  let revision = 0;
  let cached: { value: T; expiresAt: number } | undefined;
  let pending: Promise<T> | undefined;

  function clear() {
    revision += 1;
    cached = undefined;
    pending = undefined;
  }

  function read(scope: unknown, load: () => Promise<T>, signal?: AbortSignal): Promise<T> {
    if (signal?.aborted) return Promise.reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
    if (owner !== scope) {
      clear();
      owner = scope;
    }
    const readRevision = revision;
    let shared: Promise<T>;
    if (cached && cached.expiresAt > Date.now()) {
      shared = Promise.resolve(cached.value);
    } else {
      if (!pending) {
        const request = Promise.resolve().then(load).then(value => {
          if (readRevision !== revision) throw new DOMException('Read owner changed', 'AbortError');
          cached = { value, expiresAt: Date.now() + ttlMs };
          return value;
        }).finally(() => {
          if (pending === request) pending = undefined;
        });
        pending = request;
      }
      shared = pending;
    }
    if (!signal) return shared;
    return new Promise<T>((resolve, reject) => {
      const abort = () => reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
      signal.addEventListener('abort', abort, { once: true });
      shared.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
    });
  }

  function peek(scope: unknown): T | undefined {
    if (owner !== scope || !cached || cached.expiresAt <= Date.now()) return undefined;
    return cached.value;
  }

  return { read, clear, peek };
}
